import { useEffect, useMemo, useState } from "react";
import { PermissionGate } from "../../components/PermissionGate";
import { apiGet } from "../../services/api";
import { useAuth } from "../../services/auth";
import {
  QUESTIONARIO_SOURCES,
  buildSourceAnalytics,
  formatNumber,
  formatPercent,
  formatSnapshotDate,
  inspectQuestionariosSnapshot,
  type Distribution,
  type QuestionarioSourceId,
  type QuestionarioSourceState,
  type QuestionariosSnapshot,
  type SourceMetric
} from "./questionariosModel";
import "./questionarios.css";

const SNAPSHOT_ENDPOINT = "/api/questionarios/dashboard";

export function QuestionariosPage() {
  const { token } = useAuth();
  const [snapshot, setSnapshot] = useState<QuestionariosSnapshot | null>(null);
  const [activeSourceId, setActiveSourceId] = useState<QuestionarioSourceId>("feedback");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    const injectedSnapshot = import.meta.env.DEV
      ? (window as typeof window & { __QUESTIONARIOS_SNAPSHOT__?: QuestionariosSnapshot }).__QUESTIONARIOS_SNAPSHOT__
      : undefined;

    if (!injectedSnapshot && !token) {
      setLoading(false);
      setError("A sessão autenticada ainda não está disponível.");
      return;
    }

    setLoading(true);
    setError("");
    const request = injectedSnapshot
      ? Promise.resolve(injectedSnapshot)
      : apiGet<QuestionariosSnapshot>(SNAPSHOT_ENDPOINT, token || undefined);

    void request
      .then((payload) => {
        if (!payload || typeof payload !== "object") throw new Error("O snapshot retornado não possui um contrato JSON válido.");
        setSnapshot(payload);
        const firstPublishedSource = inspectQuestionariosSnapshot(payload).sources.find((source) => source.published);
        setActiveSourceId(firstPublishedSource?.id || "feedback");
      })
      .catch((err: unknown) => {
        setSnapshot(null);
        setError(err instanceof Error ? err.message : "Falha ao carregar o snapshot protegido.");
      })
      .finally(() => setLoading(false));
  }, [requestVersion, token]);

  const inspection = useMemo(() => snapshot ? inspectQuestionariosSnapshot(snapshot) : null, [snapshot]);
  const activeSource = inspection?.sources.find((source) => source.id === activeSourceId) || null;
  const analytics = useMemo(() => activeSource ? buildSourceAnalytics(activeSource) : null, [activeSource]);

  return (
    <PermissionGate permission="questionario">
      <div className="questionarios-page">
        <header className="questionarios-hero">
          <div>
            <p className="questionarios-kicker">Escuta estudantil</p>
            <h1>Questionários</h1>
            <p>Acompanhamento integrado de satisfação, carreira, perfil de ingresso e bem-estar.</p>
          </div>
          {inspection ? (
            <div className="questionarios-freshness" title={`Geração do snapshot: ${formatSnapshotDate(inspection.generatedAt)}`}>
              <Icon name="refresh" />
              <span>Snapshot verificado</span>
              <strong>{formatSnapshotDate(inspection.generatedAt)}</strong>
            </div>
          ) : null}
        </header>

        {loading ? <QuestionariosSkeleton /> : null}

        {!loading && error ? (
          <section className="questionarios-state questionarios-state--error" role="alert">
            <span className="questionarios-state__icon"><Icon name="alert" /></span>
            <div>
              <p className="questionarios-kicker">Falha na consulta</p>
              <h2>Não foi possível acessar o snapshot protegido</h2>
              <p>{error}</p>
            </div>
            <button type="button" onClick={() => setRequestVersion((value) => value + 1)}>
              <Icon name="refresh" /> Tentar novamente
            </button>
          </section>
        ) : null}

        {!loading && !error && inspection ? (
          <>
            <SourceTabs
              sources={inspection.sources}
              activeSourceId={activeSourceId}
              onSelect={setActiveSourceId}
            />

            {inspection.publishedSourceCount && activeSource && analytics ? (
              activeSource.published ? (
                activeSource.records.length ? (
                  <QuestionarioDashboard source={activeSource} metrics={analytics.metrics} distributions={analytics.distributions} />
                ) : (
                  <PublishedEmptyState source={activeSource} />
                )
              ) : (
                <UnpublishedSourceState source={activeSource} />
              )
            ) : (
              <ContractUnavailableState inspection={inspection} />
            )}
          </>
        ) : null}
      </div>
    </PermissionGate>
  );
}

function SourceTabs({
  sources,
  activeSourceId,
  onSelect
}: {
  sources: QuestionarioSourceState[];
  activeSourceId: QuestionarioSourceId;
  onSelect: (id: QuestionarioSourceId) => void;
}) {
  return (
    <nav className="questionarios-tabs" aria-label="Tipos de questionário">
      {sources.map((source) => (
        <button
          type="button"
          key={source.id}
          className={source.id === activeSourceId ? "is-active" : ""}
          aria-pressed={source.id === activeSourceId}
          onClick={() => onSelect(source.id)}
        >
          <span className={`questionarios-tabs__icon questionarios-tabs__icon--${source.id}`}><Icon name={source.id} /></span>
          <span>
            <strong>{source.shortLabel}</strong>
            <small>{source.published ? `${formatNumber(source.records.length)} resposta${source.records.length === 1 ? "" : "s"}` : "Não publicado"}</small>
          </span>
          <i className={source.published ? "is-ready" : ""} aria-hidden="true" />
        </button>
      ))}
    </nav>
  );
}

function QuestionarioDashboard({
  source,
  metrics,
  distributions
}: {
  source: QuestionarioSourceState;
  metrics: SourceMetric[];
  distributions: Distribution[];
}) {
  return (
    <div className="questionarios-dashboard">
      <section className="questionarios-section-head">
        <div>
          <p className="questionarios-kicker">Visão consolidada</p>
          <h2>{source.label}</h2>
          <p>{source.description}</p>
        </div>
        <span className="questionarios-source-badge"><Icon name="shield" /> Fonte protegida publicada</span>
      </section>

      <section className="questionarios-metrics" aria-label={`Indicadores de ${source.label}`}>
        {metrics.map((metric) => <MetricCard key={metric.label} metric={metric} />)}
      </section>

      <section className="questionarios-chart-grid">
        {distributions.map((distribution) => (
          <DistributionPanel key={distribution.title} distribution={distribution} />
        ))}
      </section>
    </div>
  );
}

function MetricCard({ metric }: { metric: SourceMetric }) {
  return (
    <article className={`questionarios-metric questionarios-metric--${metric.tone}`}>
      <div className="questionarios-metric__head">
        <span>{metric.label}</span>
        <button
          type="button"
          className="questionarios-tooltip"
          data-tooltip={metric.calculation}
          aria-label={`Como é calculado: ${metric.calculation}`}
        >?</button>
      </div>
      <strong title={metric.value}>{metric.value}</strong>
      <small>{metric.detail}</small>
    </article>
  );
}

function DistributionPanel({ distribution }: { distribution: Distribution }) {
  return (
    <section className="questionarios-distribution">
      <header>
        <div>
          <p className="questionarios-kicker">Distribuição</p>
          <h3>{distribution.title}</h3>
          <p>{distribution.subtitle}</p>
        </div>
        <button
          type="button"
          className="questionarios-tooltip"
          data-tooltip={distribution.subtitle}
          aria-label={`Como é calculado: ${distribution.title}. ${distribution.subtitle}`}
        >?</button>
      </header>
      {distribution.items.length ? (
        <ol className="questionarios-bars" aria-label={distribution.title}>
          {distribution.items.map((item) => (
            <li key={item.label}>
              <div className="questionarios-bars__label" title={item.label}>{item.label}</div>
              <div className="questionarios-bars__track" aria-hidden="true">
                <span style={{ inlineSize: `${Math.max(item.percent, 2)}%` }} />
              </div>
              <strong>{formatNumber(item.value)} <span>({formatPercent(item.percent)})</span></strong>
            </li>
          ))}
        </ol>
      ) : (
        <div className="questionarios-chart-empty">
          <Icon name="empty" />
          <span>Não há respostas válidas para esta distribuição.</span>
        </div>
      )}
    </section>
  );
}

function ContractUnavailableState({ inspection }: { inspection: ReturnType<typeof inspectQuestionariosSnapshot> }) {
  return (
    <section className="questionarios-contract-state">
      <div className="questionarios-contract-state__main">
        <span className="questionarios-state__icon"><Icon name="database" /></span>
        <div>
          <p className="questionarios-kicker">Integração pendente</p>
          <h2>O snapshot seguro ainda não publica respostas de questionários</h2>
          <p>
            A consulta autenticada foi concluída, mas ainda não há coleções de respostas publicadas no snapshot seguro.
            Nenhum indicador foi estimado ou preenchido artificialmente.
          </p>
          <div className="questionarios-contract-state__meta">
            <span><Icon name="check" /> Endpoint protegido acessível</span>
            <span><Icon name="clock" /> {formatSnapshotDate(inspection.generatedAt)}</span>
          </div>
        </div>
      </div>

      <div className="questionarios-contract-state__requirement">
        <strong>O que falta para liberar as análises</strong>
        <p>Publicar as quatro coleções em um snapshot protegido e autorizar sua leitura com a permissão de Questionários.</p>
      </div>

      <div className="questionarios-source-grid">
        {QUESTIONARIO_SOURCES.map((source) => (
          <article key={source.id}>
            <span className={`questionarios-tabs__icon questionarios-tabs__icon--${source.id}`}><Icon name={source.id} /></span>
            <div>
              <h3>{source.shortLabel}</h3>
              <p>{source.description}</p>
              <code>{source.collection}</code>
            </div>
            <span className="questionarios-source-status">Aguardando publicação</span>
          </article>
        ))}
      </div>
    </section>
  );
}

function PublishedEmptyState({ source }: { source: QuestionarioSourceState }) {
  return (
    <section className="questionarios-state" role="status">
      <span className="questionarios-state__icon"><Icon name="empty" /></span>
      <div>
        <p className="questionarios-kicker">Fonte publicada</p>
        <h2>Nenhuma resposta em {source.shortLabel}</h2>
        <p>A coleção está presente no snapshot, mas foi publicada sem registros.</p>
      </div>
    </section>
  );
}

function UnpublishedSourceState({ source }: { source: QuestionarioSourceState }) {
  return (
    <section className="questionarios-state" role="status">
      <span className="questionarios-state__icon"><Icon name="database" /></span>
      <div>
        <p className="questionarios-kicker">Fonte indisponível</p>
        <h2>{source.shortLabel} ainda não foi publicado</h2>
        <p>O snapshot consultado não contém a coleção <code>{source.collection}</code>.</p>
      </div>
    </section>
  );
}

function QuestionariosSkeleton() {
  return (
    <div className="questionarios-skeleton" aria-label="Carregando dados de questionários" role="status">
      <div className="questionarios-skeleton__tabs" />
      <div className="questionarios-skeleton__metrics">
        {[0, 1, 2, 3].map((item) => <span key={item} />)}
      </div>
      <div className="questionarios-skeleton__panels"><span /><span /></div>
    </div>
  );
}

type IconName = QuestionarioSourceId | "refresh" | "alert" | "shield" | "empty" | "database" | "check" | "clock";

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, JSX.Element> = {
    feedback: <><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" /><path d="M8 9h8M8 13h5" /></>,
    carreira: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18M10 12v2h4v-2" /></>,
    procalouro: <><path d="m2 10 10-5 10 5-10 5Z" /><path d="M6 12v5c3 2 9 2 12 0v-5M22 10v6" /></>,
    saude: <><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" /></>,
    refresh: <><path d="M20 11a8 8 0 1 0 2 5.3" /><path d="M20 4v7h-7" /></>,
    alert: <><path d="M10.3 2.9 1.8 17a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 2.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></>,
    empty: <><path d="M4 4h16v16H4z" /><path d="M8 9h8M8 13h5" /></>,
    database: <><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}
